/**
 * @file     ToolAction.tsx
 * @purpose  The two result actions in the family 7 tools: a filled primary
 *           (a link, usually to homes at the result) and an outlined
 *           secondary (a link or a button that opens a sheet).
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import { focusRingSx, hubColors, hubRadius, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';

type ToolActionProps = {
  children: React.ReactNode;
  tone?: 'primary' | 'secondary';
  href?: string;
  onClick?: () => void;
  id?: string;
  ariaHaspopup?: 'dialog';
};

export function ToolAction({ children, tone = 'primary', href, onClick, id, ariaHaspopup }: ToolActionProps) {
  const primary = tone === 'primary';
  const sx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    px: 5,
    m: 0,
    border: `1px solid ${primary ? hubColors.interactivePrimary : hubColors.borderStrong}`,
    borderRadius: `${hubRadius.control}px`,
    backgroundColor: primary ? hubColors.interactivePrimary : 'transparent',
    color: primary ? hubColors.textOnPrimary : hubColors.textPrimary,
    font: 'inherit',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.25,
    textAlign: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: transition('background-color', 'border-color'),
    '&:hover': primary
      ? { backgroundColor: hubColors.primaryHover, borderColor: hubColors.primaryHover }
      : { borderColor: hubColors.textPrimary },
    ...focusRingSx,
    ...reducedMotionSx,
  } as const;

  if (href) {
    return (
      <Box component={Link} href={href} id={id} onClick={onClick} sx={sx}>
        {children}
      </Box>
    );
  }
  return (
    <Box component="button" type="button" id={id} onClick={onClick} aria-haspopup={ariaHaspopup} sx={sx}>
      {children}
    </Box>
  );
}
